import { Dtos } from "@inrev/common";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { logError } from "../../utils/error";
import { useRequest } from "../../utils/request";
import { SuretyEntityIdMap } from "../types";
import { followerFromAuthenticatedUser } from "./followerFromAuthenticatedUser";
import { followersQueryKey } from "./followers.keys";

export const useRemoveAgentFollower = <T extends keyof SuretyEntityIdMap>(
	url: string,
	type: T,
	id: SuretyEntityIdMap[T],
) => {
	const { _delete } = useRequest();
	const queryClient = useQueryClient();
	const { triggerErrorModal } = useGlobalErrorMessageModal();
	const { user } = useAuthenticatedUser();

	const mutation = useMutation({
		mutationFn: async (agent?: Dtos.Agent.Preview.Get.Response) =>
			await _delete(`${url}/${agent?.id ?? user.id}`, undefined, "none"),
		onMutate: (agent?: Dtos.Agent.Preview.Get.Response) => {
			const followerToRemove: Dtos.Follower.Get.Response = agent
				? Dtos.Follower.fromAgentPreview(agent)
				: followerFromAuthenticatedUser(user);

			queryClient.setQueryData(
				followersQueryKey(type, id),
				(oldData: Dtos.Follower.Get.Response[] | undefined) => {
					if (!!oldData) {
						return oldData.filter((follower) => follower.id !== followerToRemove.id);
					}
					return [];
				},
			);

			return followerToRemove;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: followersQueryKey(type, id) });
		},
		onError: (error: Error, _variables, followerToRemove) => {
			logError(error);
			triggerErrorModal(error);
			if (!!followerToRemove) {
				queryClient.setQueryData(
					followersQueryKey(type, id),
					(oldData: Dtos.Follower.Get.Response[] | undefined) => {
						if (!!oldData) {
							return [...oldData, followerToRemove];
						}
						return [followerToRemove];
					},
				);
			}
		},
	});

	return { removeAgentFollower: mutation.mutate, removeAgentFollowerLoading: mutation.isPending };
};
